import { Link } from "react-router-dom";

export default function TermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-2xl font-bold mb-4">Terms of Service</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: February 21, 2026</p>

      <p className="text-gray-700 leading-relaxed mb-4">
        These terms govern your use of Echonour (echonour.com). By using our service, you agree to these terms. They're written in plain English.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">What Echonour Does</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Echonour is an AI-powered transcription and analysis service. You provide a URL to a YouTube video, podcast, or audio file, and we generate a transcript with speaker identification. You can also chat with your transcripts using AI to ask questions and get answers with citations.
      </p>

      <h2 id="early-access" className="text-xl font-semibold mt-8 mb-3">🎉 Early Access Terms</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        <strong>Echonour is currently free during early access.</strong> We're fine-tuning the experience and not charging while we're in testing. This free period will end when we launch billing — we'll give you notice before that happens.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4">
        During early access, fair use limits apply to prevent abuse and ensure the service remains available for everyone:
      </p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>Transcription:</strong> Up to 20 hours per 30-day period</li>
        <li><strong>Chat:</strong> Up to 100 messages per day</li>
        <li><strong>Research mode:</strong> Up to 60 tool calls per day</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4">
        These limits exist to prevent bots and automated abuse — normal human usage won't hit them. If you have a legitimate need that exceeds these limits, contact us at{" "}
        <a href="mailto:support@echonour.com" className="text-blue-600 underline hover:text-blue-800">support@echonour.com</a>.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4">
        We may suspend accounts that appear to be abusing the free access period. We reserve the right to modify or end the early access program at any time.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Your Responsibilities</h2>
      <p className="text-gray-700 leading-relaxed mb-4">When using Echonour, you agree to:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li>Only transcribe content you have the right to access</li>
        <li>Not use Echonour for content that violates copyright laws</li>
        <li>Keep your account credentials secure</li>
        <li>Maintain only one account per person</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4 font-semibold italic">
        Important: We're a transcription tool, not a content host. You're responsible for ensuring you have the right to transcribe the content you submit. We're not liable for copyright issues with your source material.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Billing & Payments</h2>
      <p className="text-gray-700 leading-relaxed mb-4">Echonour offers flexible pricing:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>Free tier:</strong> 45 minutes of transcription for new users</li>
        <li><strong>Guest access:</strong> 45 minutes tracked by IP address (no account needed)</li>
        <li><strong>PAYG:</strong> No subscription, $0.90/hr transcription, 20 chat messages/month</li>
        <li><strong>Base ($15/mo):</strong> 20 hrs/mo, 100 chat messages, $0.60/hr top-up</li>
        <li><strong>Pro ($30/mo):</strong> 45 hrs/mo, unlimited chat, $0.52/hr top-up</li>
        <li><strong>Studio ($55/mo):</strong> 90 hrs/mo, unlimited chat, $0.48/hr top-up</li>
        <li><strong>Elite ($90/mo):</strong> 150 hrs/mo, unlimited chat, $0.44/hr top-up</li>
        <li><strong>Annual plans:</strong> Save 15% with all hours credited upfront</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4">
        All payments are processed by Paddle, our Merchant of Record. Unused monthly hours roll over up to your tier's cap. Annual subscribers receive all hours upfront with no monthly limits.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Refund Policy</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Payments are processed by Paddle, our Merchant of Record. Paddle's refund policy applies to all purchases.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4">
        <strong>14-day cancellation right:</strong> If you're a consumer, you have the right to cancel within 14 days of purchase for a full refund — unless you've already started using the service (downloaded transcripts or consumed minutes).
      </p>
      <p className="text-gray-700 leading-relaxed mb-4">
        <strong>After 14 days:</strong> Refunds are provided at Paddle's discretion on a case-by-case basis.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4">
        <strong>Failed transcriptions:</strong> If a transcription fails due to our error (not source unavailability), you won't be charged.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4">
        <strong>How to request:</strong> Contact Paddle directly through your purchase receipt, or email{" "}
        <a href="mailto:support@echonour.com" className="text-blue-600 underline hover:text-blue-800">support@echonour.com</a>{" "}
        and we'll help facilitate.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Chat Feature</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        The Chat feature lets you ask questions about your transcripts using AI. Chat uses your transcription minutes for processing. You can optionally bring your own API keys (BYOK) for providers like OpenAI, Anthropic, or Google — these are encrypted and stored securely.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Your Data</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Transcripts are stored on our servers to provide the service. You own your transcripts — you can export or delete them anytime. See our{" "}
        <Link to="/privacy" className="text-blue-600 underline hover:text-blue-800">Privacy Policy</Link>{" "}
        for full details on data handling.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Prohibited Uses</h2>
      <p className="text-gray-700 leading-relaxed mb-4">Don't use Echonour to:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li>Bulk scrape or mass-download content</li>
        <li>Resell transcripts as a competing service</li>
        <li>Automate requests beyond normal usage patterns</li>
        <li>Circumvent usage limits or billing</li>
        <li>Transcribe illegal content</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4">
        We may suspend accounts that abuse the service.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Service Availability</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Echonour is provided "as is". We work hard to keep the service reliable, but we can't guarantee 100% uptime. We may have maintenance windows, and third-party services we depend on (YouTube, podcast hosts) may occasionally be unavailable.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Accuracy Disclaimer</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Transcription is performed by AI and isn't perfect. Speaker identification is our best-effort attempt based on available information. Chat answers are AI-generated and may contain errors. We don't guarantee accuracy — always review outputs if precision matters for your use case.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Limitation of Liability</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        To the maximum extent permitted by law, Echonour is not liable for indirect, incidental, or consequential damages arising from your use of the service. Our total liability is limited to the amount you've paid us in the past 12 months.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Account Termination</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        <strong>By us:</strong> We may suspend or terminate accounts that violate these terms, abuse the service, or engage in fraudulent activity.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4">
        <strong>By you:</strong> Contact{" "}
        <a href="mailto:support@echonour.com" className="text-blue-600 underline hover:text-blue-800">support@echonour.com</a>{" "}
        to request account deletion. We'll remove all your data within 30 days.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Changes to Terms</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        We may update these terms occasionally. For significant changes, we'll notify you by email before they take effect. Continued use after changes means you accept the new terms.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Governing Law</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        These terms are governed by the laws of the United Arab Emirates. Any disputes will be resolved in the courts of Dubai, UAE.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Contact</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Questions about these terms? Email us at{" "}
        <a href="mailto:support@echonour.com" className="text-blue-600 underline hover:text-blue-800">support@echonour.com</a>.
      </p>
    </div>
  );
}
